import { MenuItem, TextField, Typography } from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Dimmer from 'components/base/Dimmer';
import FlexContainer from 'components/FlexContainer';
import DeleteIconButton from 'components/iconButtons/DeleteIconButton';
import { InvitationFooter } from 'components/invitation/InvitationFooter';
import { CloseModal } from 'components/modals/types';
import FormattedMessage from 'features/i18n/FormattedMessage';
import { LottieLoadingAnimation } from 'features/loading-spinner/LottieLoadingAnimation';
import { useWorkspaceInviteSendMutation } from 'features/workspaces/invitations/useWorkspaceInviteSendMutation';
import { useGetWorkspaceInfo } from 'features/workspaces/useGetWorkspaceInfo';
import { ReactElement, useState } from 'react';
import { WorkspaceRole } from 'services/backofficeIntegration/http/endpoints/workspace/httpGetWorkspaceMembersOfWorkspace';
import styled from 'styled-components';
import useTr from 'utils/hooks/useTr';
import isValidEmail from 'utils/isValidEmail';

type Props = CloseModal & {
  isSalesAssistedAccount: boolean;
  seatsLeft: number;
};

export const WorkspaceInviteModal = ({ closeModal, isSalesAssistedAccount, seatsLeft }: Props) => {
  const translate = useTr();

  const { data: workspace } = useGetWorkspaceInfo();

  const [role, setRole] = useState<WorkspaceRole>(WorkspaceRole.MEMBER);
  const [enteredEmail, setEnteredEmail] = useState<string>('');
  const [error, setError] = useState<ReactElement | null>(null);

  const { mutate: inviteUser, isPending } = useWorkspaceInviteSendMutation();

  const handleRoleChange = (event: SelectChangeEvent) => {
    setRole(event.target.value as WorkspaceRole);
  };

  if (!workspace) {
    return <LottieLoadingAnimation />;
  }

  const handleInviteUsers = () => {
    if (!workspace.id) {
      return;
    }

    if (isCustomerEmailValid(enteredEmail)) {
      inviteUser(
        {
          workspaceId: workspace.id,
          email: enteredEmail,
          role: role,
        },
        {
          onSuccess: () => {
            closeModal();
          },
        }
      );
    } else {
      setError(
        <FormattedMessage
          id="profile.users.invite_user.error.invalid_email"
          values={{ enteredEmail }}
        />
      );
    }
  };

  const isCustomerEmailValid = (email: string) => {
    if (!isValidEmail(email)) {
      return false;
    }

    return true;
  };

  return (
    <ModalBody className="modal-body">
      <Dimmer active={isPending}>
        <Header direction="row" alignItems="center" justifyContent="space-between">
          <Headline variant="h5">
            <FormattedMessage id="profile.users.invite_user" />
          </Headline>
          {closeModal && <DeleteIconButton onClick={closeModal} />}
        </Header>

        <ModalContent
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          gap="small"
        >
          <TextField
            value={enteredEmail}
            variant="outlined"
            onChange={e => setEnteredEmail(e.target.value)}
            placeholder={translate('profile.users.invite_user.input_placeholder')}
            autoFocus={true}
            helperText={<FormattedMessage id="profile.users.invite_user.input_help_text" />}
          />

          <Select
            value={role}
            onChange={handleRoleChange}
            sx={theme => ({
              '.MuiInputBase-input': {
                paddingTop: theme.spacing(1),
                paddingBottom: theme.spacing(1),
              },
            })}
            renderValue={value => (
              <Typography>
                {value === WorkspaceRole.ADMIN ? (
                  <FormattedMessage id="customer.role.admin" />
                ) : (
                  <FormattedMessage id="customer.role.member" />
                )}
              </Typography>
            )}
          >
            <MenuItem value={WorkspaceRole.ADMIN}>
              <FormattedMessage id="customer.role.admin" />
            </MenuItem>
            <MenuItem value={WorkspaceRole.MEMBER}>
              <FormattedMessage id="customer.role.member" />
            </MenuItem>
          </Select>
        </ModalContent>

        {error && (
          <FlexContainer justifyContent="flex-start" style={{ marginBottom: 16 }}>
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          </FlexContainer>
        )}
        <InvitationFooter
          isSalesAssistedAccount={isSalesAssistedAccount}
          isDisabled={isPending}
          handleInviteUsers={handleInviteUsers}
          seatsLeft={seatsLeft}
        />
      </Dimmer>
    </ModalBody>
  );
};

const ModalBody = styled.div`
  position: relative;
  padding: ${({ theme }) => theme.spacings.xmedium};
`;

const ModalContent = styled(FlexContainer)`
  margintop: ${({ theme }) => theme.spacings.three};
  marginbottom: ${({ theme }) => theme.spacings.one};
`;

const Header = styled(FlexContainer)`
  width: 100%;
`;

const Headline = styled(Typography)`
  && {
    color: ${({ theme }) => theme.colors.blackHighEmphasis};
  }
`;
